<template>
    <div class="audio-wave-player">
        <div class="audio-controls">
            <div class="btn-icon-2" @click="togglePlay">
                <v-icon color="#0052CC" size="24">{{ isPlaying ? 'mdi-pause' : 'mdi-play' }}</v-icon>
            </div>
            <div class="time-display">{{ currentTime }} / {{ duration }}</div>

            <!-- Audio controls -->
            <div class="btn-icon-2" @click="rewindAudio(10)">
                <v-icon color="#0052CC" size="20">mdi-rewind-10</v-icon>
            </div>
            <div class="btn-icon-2" @click="forwardAudio(10)">
                <v-icon color="#0052CC" size="20">mdi-fast-forward-10</v-icon>
            </div>
            <div class="btn-icon-2" @click="toggleMute">
                <v-icon color="#0052CC" size="20">{{ isMuted ? 'mdi-volume-off' : 'mdi-volume-high' }}</v-icon>
            </div>
            <div class="volume-slider">
                <v-slider
                    v-model="volume"
                    min="0"
                    max="100"
                    hide-details
                    class="ma-0 pa-0"
                    @change="updateVolume"
                ></v-slider>
            </div>
            <div class="btn-icon-2" @click="toggleLoop">
                <v-icon :color="isLooping ? '#0052CC' : '#727885'" size="20">mdi-repeat</v-icon>
            </div>
            <div class="btn-icon-2" @click="downloadAudio">
                <v-icon color="#0052CC" size="20">mdi-download</v-icon>
            </div>

            <!-- Playback speed control -->
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <div class="btn-icon-2" v-bind="attrs" v-on="on">
                        <div class="speed-display">{{ playbackSpeed }}x</div>
                    </div>
                </template>
                <v-list dense>
                    <v-list-tile
                        v-for="speed in playbackSpeeds"
                        :key="speed"
                        @click="setPlaybackSpeed(speed)"
                    >
                        <v-list-tile-title>{{ speed }}x</v-list-tile-title>
                    </v-list-tile>
                </v-list>
            </v-menu>
        </div>
        <div id="waveform" ref="waveform"></div>
    </div>
</template>

<script>
import WaveSurfer from 'wavesurfer.js';

export default {
    name: 'AudioWavePlayer',
    props: {
        audioSrc: {
            type: String,
            required: true
        },
        audioId: {
            type: String,
            default: ''
        },
        audioData: {
            type: Blob,
            default: null
        }
    },
    data() {
        return {
            wavesurfer: null,
            isPlaying: false,
            isMuted: false,
            isLooping: false,
            volume: 75,
            currentTime: '00:00',
            duration: '00:00',
            playbackSpeed: 1.0,
            playbackSpeeds: [0.5, 0.75, 1.0, 1.25, 1.5, 2.0],
            zoomLevel: 1,
            minZoom: 0.5,
            maxZoom: 3,
            zoomStep: 0.1
        };
    },
    watch: {
        audioSrc: {
            handler(newSrc) {
                if (this.wavesurfer) {
                    if (newSrc) {
                        this.wavesurfer.load(newSrc);
                    } else {
                        this.wavesurfer.destroy()
                        this.wavesurfer = null
                        this.currentTime = '00:00'
                        this.duration = '00:00'
                    }
                } else if (newSrc) {
                    this.initWaveSurfer()
                }
            },
        }
    },
    mounted() {
        this.initWaveSurfer();

        // Add wheel event listener for zoom
        if (this.$refs.waveform) {
            this.$refs.waveform.addEventListener('wheel', this.handleWheel, {passive: false});
        }
    },
    beforeDestroy() {
        // Remove wheel event listener
        if (this.$refs.waveform) {
            this.$refs.waveform.removeEventListener('wheel', this.handleWheel);
        }

        // Destroy wavesurfer instance
        if (this.wavesurfer) {
            this.wavesurfer.destroy();
        }
    },
    methods: {
        initWaveSurfer() {
            // Create WaveSurfer instance
            this.wavesurfer = WaveSurfer.create({
                container: this.$refs.waveform,
                waveColor: ['#4F76A6', '#0052CC'], // Different colors for each channel
                progressColor: ['#2E5492', '#003D99'],
                cursorColor: '#4a4a4a',
                barWidth: 1,
                cursorWidth: 1,
                height: 25,
                barGap: 1,
                responsive: true,
                normalize: true,
                splitChannels: true,
            });

            // Add event listeners
            this.wavesurfer.on('ready', () => {
                this.duration = this.formatTime(this.wavesurfer.getDuration());
                this.wavesurfer.setVolume(this.volume / 100);
                this.wavesurfer.setPlaybackRate(this.playbackSpeed);
            });

            this.wavesurfer.on('audioprocess', () => {
                this.currentTime = this.formatTime(this.wavesurfer.getCurrentTime());
            });

            this.wavesurfer.on('play', () => {
                this.isPlaying = true;
            });

            this.wavesurfer.on('pause', () => {
                this.isPlaying = false;
            });

            this.wavesurfer.on('finish', () => {
                if (!this.isLooping) {
                    this.isPlaying = false;
                }
            });

            // Load audio if source is available
            if (this.audioSrc) {
                this.wavesurfer.load(this.audioSrc);
            }
        },
        togglePlay() {
            if (this.wavesurfer) {
                this.wavesurfer.playPause();
            }
        },
        toggleMute() {
            if (this.wavesurfer) {
                this.isMuted = !this.isMuted;
                this.wavesurfer.setMute(this.isMuted);
            }
        },
        updateVolume() {
            if (this.wavesurfer) {
                this.wavesurfer.setVolume(this.volume / 100);
            }
        },
        rewindAudio(seconds) {
            if (this.wavesurfer) {
                const currentTime = this.wavesurfer.getCurrentTime();
                const newTime = Math.max(0, currentTime - seconds);
                this.wavesurfer.seekTo(newTime / this.wavesurfer.getDuration());
            }
        },
        forwardAudio(seconds) {
            if (this.wavesurfer) {
                const currentTime = this.wavesurfer.getCurrentTime();
                const newTime = Math.min(this.wavesurfer.getDuration(), currentTime + seconds);
                this.wavesurfer.seekTo(newTime / this.wavesurfer.getDuration());
            }
        },
        toggleLoop() {
            this.isLooping = !this.isLooping;

            if (this.wavesurfer) {
                // If we're enabling loop, add a finish event handler
                if (this.isLooping) {
                    // Remove any existing finish event handlers to avoid duplicates
                    this.wavesurfer.un('finish');

                    // Add a new finish event handler that restarts playback
                    this.wavesurfer.on('finish', () => {
                        this.wavesurfer.seekTo(0);
                        this.wavesurfer.play();
                    });
                } else {
                    // If we're disabling loop, remove the finish event handler
                    // and add back the default one that just sets isPlaying to false
                    this.wavesurfer.un('finish');
                    this.wavesurfer.on('finish', () => {
                        this.isPlaying = false;
                    });
                }
            }
        },
        setPlaybackSpeed(speed) {
            this.playbackSpeed = speed;
            if (this.wavesurfer) {
                this.wavesurfer.setPlaybackRate(speed);
            }
        },
        formatTime(timeInSeconds) {
            if (!timeInSeconds) return '00:00';

            const minutes = Math.floor(timeInSeconds / 60);
            const seconds = Math.floor(timeInSeconds % 60);
            return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
        },
        downloadAudio() {
            if (this.audioData) {
                this.$emit('download');
            }
        },
        handleWheel(e) {
            // Check if Ctrl key is pressed (common for zoom operations)
            if (e.ctrlKey || e.metaKey) {
                e.preventDefault();

                // Determine zoom direction
                const direction = e.deltaY < 0 ? 1 : -1;

                // Calculate new zoom level
                let newZoom = this.zoomLevel + (direction * this.zoomStep);

                // Clamp zoom level between min and max
                newZoom = Math.max(this.minZoom, Math.min(this.maxZoom, newZoom));

                // Apply zoom
                this.applyZoom(newZoom);
            }
        },
        applyZoom(zoomLevel) {
            this.zoomLevel = zoomLevel;

            // Apply zoom to wavesurfer if it exists
            if (this.wavesurfer) {
                // Update wavesurfer zoom
                this.wavesurfer.zoom(Math.floor(zoomLevel * 50));
            }
        }
    }
};
</script>

<style scoped lang="scss">
.audio-wave-player {
    padding: 5px;
    background: #f5f7fa;
    border-radius: 5px;
    margin: 5px 0;

    .audio-controls {
        display: flex;
        align-items: center;
        margin-bottom: 12px;

        .btn-icon-2 {
            width: 32px;
            height: 32px;
            border-radius: 4px;
            display: flex;
            align-items: center;
            justify-content: center;
            cursor: pointer;
            margin-right: 8px;

            &:hover {
                background-color: rgba(0, 82, 204, 0.1);
            }
        }

        .time-display {
            font-family: 'open sans', serif;
            font-size: 14px;
            color: #4A4A4A;
            margin: 0 12px;
            min-width: 80px;
        }

        .volume-slider {
            width: 100px;
            margin: 0 10px 0 5px;
        }

        .speed-display {
            font-family: 'open sans', serif;
            font-size: 14px;
            color: #0052CC;
            padding: 0 4px;
        }
    }

    #waveform {
        height: 50px;

        ::v-deep {
            wave {
                overflow: hidden;
                border-radius: 4px;
            }
        }
    }
}

// Style for active speed in dropdown
.active-speed {
    background-color: #EBF2FF !important;

    ::v-deep {
        .v-list-item__title {
            color: #0052CC !important;
            font-weight: bold;
        }
    }
}
</style>
